import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import { BtnSk } from '../components/Skeleton';

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    if (authService.isAuthenticated()) {
      // Redirect based on role
      if (authService.isAdmin()) {
        navigate('/');
      } else if (authService.isSeller()) {
        navigate('/seller-sales');
      } else {
        navigate('/');
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (!credentials.email || !credentials.password) {
        throw new Error('Please enter both email and password');
      }
      
      // Call the login function from authService
      await authService.login(credentials.email, credentials.password);
      
      // Redirect based on user role
      const user = authService.getCurrentUser();
      if (user) {
        if (user.role === 'admin') {
          navigate('/');
        } else if (user.role === 'seller') {
          navigate('/seller-sales');
        } else {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    } catch (err) {
      // Handle login errors
      if (err.response && err.response.data) {
        if (err.response.data.non_field_errors) {
          setError(err.response.data.non_field_errors[0]);
        } else {
          setError('Login failed: ' + (err.message || 'Unknown error'));
        }
      } else {
        setError('Login failed: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Define animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  const logoVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.7,
        yoyo: Infinity,
        ease: "easeInOut"
      }
    },
    hover: { 
      scale: 1.1,
      rotate: [0, -5, 5, -5, 0],
      transition: { duration: 0.5 }
    }
  };

  // Background particles animation
  const particles = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 10 + 5,
    duration: Math.random() * 20 + 10
  }));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">      {/* Background animated particles */}
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-red-600 opacity-10"
          style={{ 
            left: `${particle.x}%`, 
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`
          }}
          animate={{ 
            x: [0, Math.random() * 100 - 50],
            y: [0, Math.random() * 100 - 50],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{ 
            duration: particle.duration,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      ))}
      
      <motion.div 
        className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-lg shadow-2xl border border-red-600 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <motion.img 
            src={require('../assets/icon.png')} 
            alt="Kipara POS Logo"
            className="mx-auto w-32 h-auto filter drop-shadow-lg" 
            variants={logoVariants}
            whileHover="hover"
          />
          <motion.div 
            className="mt-6 text-center"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-extrabold text-white">Sign in to Kipara <span className="text-red-500">POS</span></h2>
            <p className="mt-2 text-sm text-gray-300">Manage your business with ease</p>
          </motion.div>
        </motion.div>
        <motion.form 
          className="mt-8 space-y-6" 
          onSubmit={handleSubmit}
          variants={itemVariants}
        >
          <input type="hidden" name="remember" defaultValue="true" />
          <div className="space-y-4">
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email ou nom d'utilisateur
              </label>
              <input
                id="email"
                name="email"
                type="text"
                autoComplete="username"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-600 bg-gray-700 placeholder-gray-400 text-white rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all duration-200 sm:text-sm"
                placeholder="Email ou nom d'utilisateur"
                value={credentials.email}
                onChange={handleChange}
              />
            </motion.div>
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-600 bg-gray-700 placeholder-gray-400 text-white rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all duration-200 sm:text-sm"
                placeholder="Enter your password"
                value={credentials.password}
                onChange={handleChange}
              />
            </motion.div>
          </div>

          {error && (
            <motion.div 
              className="bg-red-900 border border-red-700 rounded-md p-3"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-red-300 text-sm">{error}</p>
            </motion.div>
          )}

          <motion.div 
            className="flex items-center justify-between"
            variants={itemVariants}
          >
            <div className="flex items-center">
              <motion.input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-5 w-5 text-red-600 focus:ring-gray-900 border-gray-600 rounded cursor-pointer"
                whileTap={{ scale: 0.9 }}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300 cursor-pointer">
                Remember me
              </label>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <motion.button
              type="submit"
              disabled={isLoading}
              className="btn-p group relative w-full flex justify-center py-3 px-4 text-sm transition-all duration-300"
              whileHover={!isLoading ? { scale: 1.03 } : {}}
              whileTap={!isLoading ? { scale: 0.97 } : {}}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <BtnSk />
                  Signing in...
                </span>
              ) : 'Sign in'}
            </motion.button>
          </motion.div>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default Login;
